import React, { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CardMedia,
  Checkbox,
  Chip,
  CircularProgress,
  Container,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  List,
  ListItem,
  ListItemText,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { buildEventsApiUrl } from '../config/api';
import { RootState } from '../store/store';
import { getAuthUserId } from '../utils/authUser';

type PreviewRow = {
  sourceId: string;
  sourceType: string;
  bggId: number;
  title: string;
  yearPublished?: number | null;
  thumbnailUrl?: string;
  imageUrl?: string;
  metadata: {
    description?: string;
    categories?: string[];
    mechanics?: string[];
    families?: string[];
    minPlayers?: number | null;
    maxPlayers?: number | null;
    playingTime?: number | null;
  };
  derived: {
    tags: string[];
    priceBand?: string;
    itemKind?: string;
  };
  draft: {
    selected: boolean;
    name: string;
    category: string;
    description: string;
    condition: string;
    notes: string;
  };
  warnings?: string[];
  duplicateHints?: { kind: string; message: string; items?: { id: string; name: string }[] }[];
};

const normalizeBggUsernameInput = (input: string) => {
  let value = String(input || '').trim();
  if (!value) return '';
  value = value.replace(/^@+/, '');
  const urlMatch = value.match(/boardgamegeek\.com\/collection\/user\/([^/?#]+)/i);
  if (urlMatch?.[1]) {
    value = urlMatch[1];
  }
  return value.replace(/^@+/, '').trim();
};

const RETRY_DELAY_MS = 3000;
const MAX_RETRY_ATTEMPTS = 10;
const ITEM_CONDITIONS = ['Excellent', 'Very Good', 'Good', 'Fair', 'Poor'];

const wait = (ms: number) => new Promise((resolve) => window.setTimeout(resolve, ms));

const BggImportPreviewPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useSelector((state: RootState) => state.auth);
  const userId = getAuthUserId(user as any);
  const [username, setUsername] = useState('');
  const [ownedOnly, setOwnedOnly] = useState(true);
  const [includeExpansions, setIncludeExpansions] = useState(false);
  const [includeAccessories, setIncludeAccessories] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [summary, setSummary] = useState<any>(null);
  const [rows, setRows] = useState<PreviewRow[]>([]);
  const [search, setSearch] = useState('');
  const [statusMessage, setStatusMessage] = useState('');
  const [commitLoading, setCommitLoading] = useState(false);
  const [commitSummary, setCommitSummary] = useState<any>(null);
  const [bulkCondition, setBulkCondition] = useState('');
  const [bulkNotes, setBulkNotes] = useState('');
  const [redirectCountdown, setRedirectCountdown] = useState<number | null>(null);

  const handleLoadPreview = async () => {
    const normalizedUsername = normalizeBggUsernameInput(username);
    if (!normalizedUsername) {
      setError('BGG username is required');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setStatusMessage('');
      setSummary(null);
      setRows([]);
      setUsername(normalizedUsername);
      const params = new URLSearchParams({
        username: normalizedUsername,
        ownedOnly: String(ownedOnly),
        includeExpansions: String(includeExpansions),
        includeAccessories: String(includeAccessories),
      });
      if (userId) {
        params.set('userId', userId);
      }

      for (let attempt = 1; attempt <= MAX_RETRY_ATTEMPTS; attempt += 1) {
        const response = await fetch(buildEventsApiUrl(`/api/imports/bgg/collection-preview?${params.toString()}`));
        const data = await response.json();

        if (response.ok) {
          setSummary(data.summary || null);
          setRows((data.rows || []).map((row: PreviewRow) => {
            const normalizedRow = normalizeDraftRow(row);
            return {
              ...normalizedRow,
              draft: {
                ...normalizedRow.draft,
                selected: normalizedRow.duplicateHints?.some((hint) => hint.kind === 'bgg-id') ? false : normalizedRow.draft.selected,
              },
            };
          }));
          setStatusMessage('');
          return;
        }

        const isPending = response.status === 202 || data.code === 'BGG_COLLECTION_PENDING';
        if (isPending && attempt < MAX_RETRY_ATTEMPTS) {
          setStatusMessage(`BGG is preparing your collection… retrying automatically (${attempt}/${MAX_RETRY_ATTEMPTS})`);
          await wait(RETRY_DELAY_MS);
          continue;
        }

        throw new Error(
          isPending
            ? 'BGG is still preparing your collection. Please try again in a minute.'
            : (data.error || 'Failed to load BGG import preview')
        );
      }
    } catch (err: any) {
      setError(err.message || 'Failed to load BGG import preview');
      setSummary(null);
      setRows([]);
      setStatusMessage('');
    } finally {
      setLoading(false);
    }
  };

  const visibleRows = useMemo(() => {
    const query = search.trim().toLowerCase();
    return rows.filter((row) => {
      const haystack = [
        row.title,
        row.draft?.name,
        row.draft?.description,
        ...(row.derived?.tags || []),
        ...(row.metadata?.categories || []),
        ...(row.metadata?.mechanics || []),
      ]
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return !query || haystack.includes(query);
    });
  }, [rows, search]);

  const updateRow = (sourceId: string, patch: Partial<PreviewRow['draft']>) => {
    setRows((current) => current.map((row) => (
      row.sourceId === sourceId
        ? { ...row, draft: { ...row.draft, ...patch } }
        : row
    )));
  };

  const normalizeDraftRow = (row: PreviewRow): PreviewRow => ({
    ...row,
    draft: {
      selected: row.draft?.selected ?? true,
      name: row.draft?.name || row.title || '',
      category: row.draft?.category || 'Tabletop Games',
      description: row.draft?.description || row.metadata?.description || '',
      condition: row.draft?.condition || 'Good',
      notes: row.draft?.notes || '',
    },
  });

  const updateVisibleRows = (selected: boolean) => {
    const visibleIds = new Set(visibleRows.map((row) => row.sourceId));
    setRows((current) => current.map((row) => (
      visibleIds.has(row.sourceId)
        ? { ...row, draft: { ...row.draft, selected } }
        : row
    )));
  };

  const selectBaseGamesOnly = () => {
    setRows((current) => current.map((row) => ({
      ...row,
      draft: {
        ...row.draft,
        selected: row.derived?.itemKind === 'base-game'
      }
    })));
  };

  const excludeByType = (itemKind: string) => {
    setRows((current) => current.map((row) => (
      row.derived?.itemKind === itemKind
        ? { ...row, draft: { ...row.draft, selected: false } }
        : row
    )));
  };

  const applyBulkCondition = () => {
    const nextCondition = bulkCondition.trim();
    if (!nextCondition) return;
    const visibleIds = new Set(visibleRows.map((row) => row.sourceId));
    setRows((current) => current.map((row) => (
      visibleIds.has(row.sourceId)
        ? { ...row, draft: { ...row.draft, condition: nextCondition } }
        : row
    )));
  };

  const applyBulkNotes = () => {
    const nextNotes = bulkNotes.trim();
    if (!nextNotes) return;
    const visibleIds = new Set(visibleRows.map((row) => row.sourceId));
    setRows((current) => current.map((row) => (
      visibleIds.has(row.sourceId)
        ? { ...row, draft: { ...row.draft, notes: nextNotes } }
        : row
    )));
  };

  const skipExactDuplicates = () => {
    setRows((current) => current.map((row) => (
      row.duplicateHints?.some((hint) => hint.kind === 'bgg-id')
        ? { ...row, draft: { ...row.draft, selected: false } }
        : row
    )));
  };

  const handleCommit = async () => {
    if (!userId) {
      setError('You must be signed in to build your inventory.');
      return;
    }

    const selectedRows = rows.filter((row) => row.draft?.selected);
    if (selectedRows.length === 0) {
      setError('Select at least one item to build your inventory.');
      return;
    }

    try {
      setCommitLoading(true);
      setError('');
      setCommitSummary(null);

      const commitUrl = buildEventsApiUrl('/api/imports/bgg/commit');
      const selectedRowsPayload = selectedRows.map((row) => ({
        sourceId: row.sourceId,
        sourceType: row.sourceType,
        bggId: row.bggId,
        title: row.title,
        yearPublished: row.yearPublished,
        thumbnailUrl: row.thumbnailUrl,
        imageUrl: row.imageUrl,
        metadata: {
          description: row.metadata?.description || '',
        },
        derived: {
          tags: Array.isArray(row.derived?.tags) ? row.derived.tags : [],
          priceBand: row.derived?.priceBand || '',
          itemKind: row.derived?.itemKind || '',
        },
        draft: {
          selected: Boolean(row.draft?.selected),
          name: row.draft?.name || row.title || '',
          category: row.draft?.category || 'Tabletop Games',
          description: row.draft?.description || row.metadata?.description || '',
          condition: row.draft?.condition || 'Good',
          notes: row.draft?.notes || '',
        },
      }));

      const response = await fetch(commitUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          userId,
          sourceUsername: username,
          rows: selectedRowsPayload,
        }),
      });
      const responseText = await response.text();
      let data: any = null;
      try {
        data = responseText ? JSON.parse(responseText) : null;
      } catch {
        throw new Error(`BGG commit returned non-JSON from ${commitUrl}: ${response.status} ${response.statusText}`);
      }
      if (!response.ok) throw new Error(data?.error || 'Failed to build inventory');

      setCommitSummary(data);
      const createdSourceNames = new Set((data.created || []).map((item: any) => item.name));
      setRows((current) => current.filter((row) => !createdSourceNames.has(row.draft?.name || row.title)));
      setRedirectCountdown(4);
    } catch (err: any) {
      setError(err.message || 'Failed to build inventory');
    } finally {
      setCommitLoading(false);
    }
  };

  React.useEffect(() => {
    if (redirectCountdown === null) return;
    if (redirectCountdown <= 0) {
      navigate('/dashboard?tab=my-items');
      return;
    }

    const timer = window.setTimeout(() => {
      setRedirectCountdown((current) => (current === null ? null : current - 1));
    }, 1000);

    return () => window.clearTimeout(timer);
  }, [redirectCountdown, navigate]);

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Stack spacing={3}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700 }}>Import from BoardGameGeek</Typography>
          <Typography variant="body2" color="text.secondary">
            Preview your tabletop collection, edit staged rows, and decide what should eventually become inventory.
          </Typography>
          <Typography variant="caption" color="text.secondary">
            You can enter a plain BGG username, <code>@username</code>, or paste a BoardGameGeek collection URL.
          </Typography>
        </Box>

        <Card>
          <CardContent>
            <Stack spacing={2}>
              <TextField
                label="BoardGameGeek Username"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                placeholder="e.g. kenwayne"
                disabled={loading}
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1}>
                <FormControlLabel control={<Checkbox checked={ownedOnly} onChange={(e) => setOwnedOnly(e.target.checked)} />} label="Owned only" />
                <FormControlLabel control={<Checkbox checked={includeExpansions} onChange={(e) => setIncludeExpansions(e.target.checked)} />} label="Include expansions" />
                <FormControlLabel control={<Checkbox checked={includeAccessories} onChange={(e) => setIncludeAccessories(e.target.checked)} />} label="Include accessories" />
              </Stack>
              <Stack direction="row" spacing={1}>
                <Button variant="contained" onClick={handleLoadPreview} disabled={loading}>
                  {loading ? 'Loading Preview...' : 'Load Collection Preview'}
                </Button>
                <Button variant="outlined" onClick={() => navigate('/dashboard?tab=my-items')}>
                  Back to My Items
                </Button>
              </Stack>
            </Stack>
          </CardContent>
        </Card>

        {error && <Alert severity="error">{error}</Alert>}
        {statusMessage && <Alert severity="info">{statusMessage}</Alert>}
        {loading && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <Stack spacing={1.5} alignItems="center">
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">
                {statusMessage || 'Loading BGG collection preview...'}
              </Typography>
            </Stack>
          </Box>
        )}

        {!!summary && (
          <Alert severity="info">
            Preview loaded: {summary.includedAfterFilters} rows shown out of {summary.totalFetched} fetched.
          </Alert>
        )}

        {!!commitSummary && (
          <Alert severity="success">
            <Stack spacing={1}>
              <Typography variant="body2" sx={{ fontWeight: 700 }}>
                Inventory build complete: created {commitSummary.summary?.created || 0}, skipped {commitSummary.summary?.skipped || 0}, failed {commitSummary.summary?.failed || 0}.
              </Typography>
              {redirectCountdown !== null && redirectCountdown > 0 && (
                <Typography variant="caption" color="text.secondary">
                  Redirecting to My Items in {redirectCountdown}...
                </Typography>
              )}
              {!!commitSummary.created?.length && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Created
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {commitSummary.created.slice(0, 6).map((item: any) => (
                      <ListItem key={`created-${item.id}`} sx={{ py: 0, px: 0 }}>
                        <ListItemText
                          primary={item.name}
                          secondary={item.duplicateWarnings?.[0] || null}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              {!!commitSummary.skipped?.length && (
                <Box>
                  <Typography variant="caption" sx={{ fontWeight: 700, display: 'block', mb: 0.5 }}>
                    Skipped
                  </Typography>
                  <List dense sx={{ py: 0 }}>
                    {commitSummary.skipped.slice(0, 6).map((item: any) => (
                      <ListItem key={`skipped-${item.sourceId}-${item.bggId}`} sx={{ py: 0, px: 0 }}>
                        <ListItemText
                          primary={item.title}
                          secondary={item.reason === 'duplicate-bgg-id'
                            ? `Exact BGG duplicate: ${(item.existingItems || []).map((existing: any) => existing.name).join(', ')}`
                            : item.reason}
                        />
                      </ListItem>
                    ))}
                  </List>
                </Box>
              )}
              <Stack direction="row" spacing={1}>
                <Button size="small" variant="outlined" onClick={() => navigate('/dashboard?tab=my-items')}>
                  Go to My Items Now
                </Button>
              </Stack>
            </Stack>
          </Alert>
        )}

        {rows.length > 0 && (
          <>
            <Stack spacing={2}>
              <TextField
                label="Search preview rows"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Filter by title, category, or mechanic"
              />
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} flexWrap="wrap">
                <Button variant="outlined" onClick={() => updateVisibleRows(true)}>Import All Visible</Button>
                <Button variant="outlined" onClick={() => updateVisibleRows(false)}>Clear All Visible</Button>
                <Button variant="outlined" onClick={selectBaseGamesOnly}>Select Base Games Only</Button>
                <Button variant="outlined" onClick={skipExactDuplicates}>Skip Exact Duplicates</Button>
                <Button variant="outlined" onClick={() => excludeByType('expansion')}>Exclude Expansions</Button>
                <Button variant="outlined" onClick={() => excludeByType('accessory')}>Exclude Accessories</Button>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} flexWrap="wrap">
                <FormControl size="small" sx={{ minWidth: 220 }}>
                  <InputLabel>Set condition for visible rows</InputLabel>
                  <Select
                    value={bulkCondition}
                    label="Set condition for visible rows"
                    onChange={(e) => setBulkCondition(String(e.target.value))}
                  >
                    {ITEM_CONDITIONS.map((condition) => (
                      <MenuItem key={condition} value={condition}>{condition}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button variant="outlined" onClick={applyBulkCondition}>Apply Condition</Button>
                <TextField
                  label="Set notes for visible rows"
                  value={bulkNotes}
                  onChange={(e) => setBulkNotes(e.target.value)}
                  size="small"
                  sx={{ minWidth: 280 }}
                />
                <Button variant="outlined" onClick={applyBulkNotes}>Apply Notes</Button>
              </Stack>
              <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }}>
                <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                  Showing {visibleRows.length} of {rows.length} imported rows.
                </Typography>
                <Button variant="contained" onClick={handleCommit} disabled={commitLoading}>
                  {commitLoading ? 'Building Inventory...' : 'Build My Inventory'}
                </Button>
              </Stack>
            </Stack>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              {visibleRows.map((row) => (
                <Grid item xs={12} md={6} lg={4} key={row.sourceId}>
                  <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
                    {row.thumbnailUrl && (
                      <CardMedia component="img" image={row.thumbnailUrl} alt={row.title} sx={{ height: 180, objectFit: 'contain', backgroundColor: '#f5f5f5' }} />
                    )}
                    <CardContent>
                      <Stack spacing={1.5}>
                        <Typography variant="h6" sx={{ fontWeight: 700 }}>{row.title}</Typography>
                        <Typography variant="body2" color="text.secondary">
                          {row.yearPublished || 'Unknown year'} • {row.derived?.itemKind || row.sourceType} • Value band: {row.derived?.priceBand || 'Unspecified'}
                        </Typography>
                        <TextField
                          label="Category"
                          value={row.draft.category}
                          size="small"
                          disabled
                          helperText="BGG imports default to Tabletop Games"
                        />
                        <TextField
                          label="Description"
                          value={row.draft.description}
                          onChange={(e) => updateRow(row.sourceId, { description: e.target.value })}
                          size="small"
                          multiline
                          minRows={2}
                        />
                        {!!row.duplicateHints?.length && (
                          <Alert severity={row.duplicateHints[0].kind === 'bgg-id' ? 'error' : 'warning'} sx={{ py: 0 }}>
                            <Stack spacing={0.75}>
                              <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                                <Chip
                                  size="small"
                                  color={row.duplicateHints[0].kind === 'bgg-id' ? 'error' : 'warning'}
                                  label={row.duplicateHints[0].kind === 'bgg-id' ? 'Exact duplicate' : 'Possible duplicate'}
                                />
                                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                  {row.duplicateHints[0].message}
                                </Typography>
                              </Stack>
                              {!!row.duplicateHints[0].items?.length && (
                                <Typography variant="caption" color="text.secondary">
                                  Existing item{row.duplicateHints[0].items.length === 1 ? '' : 's'}: {row.duplicateHints[0].items.map((item) => item.name).join(', ')}
                                </Typography>
                              )}
                            </Stack>
                          </Alert>
                        )}
                        <FormControlLabel
                          control={<Checkbox checked={row.draft.selected} onChange={(e) => updateRow(row.sourceId, { selected: e.target.checked })} />}
                          label="Import this item"
                        />
                        <TextField
                          label="Inventory name"
                          value={row.draft.name}
                          onChange={(e) => updateRow(row.sourceId, { name: e.target.value })}
                          size="small"
                        />
                        <FormControl size="small" fullWidth>
                          <InputLabel>Condition</InputLabel>
                          <Select
                            value={row.draft.condition}
                            label="Condition"
                            onChange={(e) => updateRow(row.sourceId, { condition: String(e.target.value) })}
                          >
                            {ITEM_CONDITIONS.map((condition) => (
                              <MenuItem key={condition} value={condition}>{condition}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                        <TextField
                          label="Notes"
                          value={row.draft.notes}
                          onChange={(e) => updateRow(row.sourceId, { notes: e.target.value })}
                          size="small"
                          multiline
                          minRows={2}
                        />
                      </Stack>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
            <Stack direction={{ xs: 'column', md: 'row' }} spacing={1} alignItems={{ xs: 'stretch', md: 'center' }} sx={{ mt: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ flexGrow: 1 }}>
                Ready to create selected items in your CrowdTrades inventory.
              </Typography>
              <Button variant="contained" onClick={handleCommit} disabled={commitLoading}>
                {commitLoading ? 'Building Inventory...' : 'Build My Inventory'}
              </Button>
            </Stack>
          </>
        )}
      </Stack>
    </Container>
  );
};

export default BggImportPreviewPage;
